/**
 * The mailbox a per-mailbox tool panel applies to (Spam & AI Filter, Filters,
 * Vacation responder, Snoozed, Labels) — rendered in the dialog HEADER, right
 * under the title, so the scope is visible where people look rather than as a
 * small field further down. It used to render nothing for single-mailbox users
 * and a plain "Account" select otherwise, which read as "these settings follow
 * the mailbox I'm viewing"; opening from All accounts silently picked the
 * primary mailbox.
 *
 * - Several mailboxes → a prominent switcher.
 * - One mailbox, or a panel pinned to one (`readOnly`) → the address as text.
 */

import { Mail } from 'lucide-react';
import type { EmailConnection } from '@/lib/api';

/** Picker value meaning "every mailbox" (panels that pass `allOption`). */
export const ALL_MAILBOXES = 'all';

interface DialogAccountPickerProps {
  accounts: EmailConnection[];
  value: string | undefined;
  onChange?: (accountId: string) => void;
  /** Show the mailbox without a switcher (the panel follows the main view). */
  readOnly?: boolean;
  /** Offer "All mailboxes" (value {@link ALL_MAILBOXES}) — for panels whose whole
   *  content can span mailboxes. */
  allOption?: boolean;
}

export function DialogAccountPicker({
  accounts,
  value,
  onChange,
  readOnly,
  allOption,
}: DialogAccountPickerProps) {
  const current = accounts.find((a) => a.accountId === value);
  if (!current && (readOnly || accounts.length <= 1)) return null;
  const switchable = !readOnly && !!onChange && accounts.length > 1;
  return (
    <div className="flex items-center gap-1.5 text-sm">
      <Mail className="h-3.5 w-3.5 shrink-0 text-primary" aria-hidden />
      <span className="text-muted-foreground">Mailbox:</span>
      {switchable ? (
        <select
          aria-label="Mailbox"
          className="h-8 min-w-0 rounded-md border border-primary/40 bg-background px-2 text-sm font-medium"
          value={value ?? ''}
          onChange={(e) => onChange?.(e.target.value)}
        >
          {allOption && <option value={ALL_MAILBOXES}>All mailboxes</option>}
          {accounts.map((a) => (
            <option key={a.accountId} value={a.accountId}>
              {a.emailAddress}
            </option>
          ))}
        </select>
      ) : (
        <span className="truncate font-medium" data-testid="dialog-mailbox">
          {current?.emailAddress}
        </span>
      )}
    </div>
  );
}
